import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  // Button,
  Paper,
  Container,
  CircularProgress,
  Chip,
  IconButton,
  // Stack,
  Card,
  CardContent,
  Fade,
  // Slide,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as RobotIcon,
  History as HistoryIcon,
  StarBorder as StarIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { theme, responsiveSpacing, responsiveFontSizes } from '../../theme/theme';
import { getAIConsultation } from '../../services/deepseekApi';
// import axios from 'axios';

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

interface ConsultationScreenProps {
  // Web version doesn't need navigation prop as we use useNavigate
}

const ConsultationScreen: React.FC<ConsultationScreenProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { astrolabe, saveConsultation, consultationHistory } = useUser();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' | 'warning' });
  const [suggestedQuestions] = useState([
    '我的命宫透露了什么信息？',
    '我的事业将如何发展？',
    '在感情方面我应该注意什么？',
    '告诉我关于我的财运',
    '我的健康需要注意什么？',
    '我如何改善运势？',
  ]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Handle initial question from navigation state
    const state = location.state as { initialQuestion?: string } | null;
    if (state?.initialQuestion) {
      setInputText(state.initialQuestion);
      handleSendMessage(state.initialQuestion);
    }
  }, [location.state]);

  useEffect(() => {
    // Auto-scroll to bottom when new messages are added
    if (messages.length > 0) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [messages]);

  const generateAIResponse = async (question: string, userAstrolabe: any, _userBirthInfo: any): Promise<{response: string, fullData: any}> => {
    try {
      // 使用 DeepSeek API 进行咨询（替换 VertexAI）
      const result = await getAIConsultation(question, userAstrolabe, [], 'zh-CN');

      if (result.success && result.response) {
        return {
          response: result.response,
          fullData: result
        };
      } else {
        throw new Error(result.error || 'Failed to get AI response');
      }
    } catch (error: any) {
      console.error('Error generating AI response:', error);
      throw new Error(error.message || 'Failed to get AI response. Please try again.');
    }
  };

  const handleSendMessage = async (messageText: string = inputText) => {
    if (!messageText.trim()) return;

    if (!astrolabe) {
      setSnackbar({
        open: true,
        message: '请先设置出生信息',
        severity: 'error'
      });
      return;
    }

    // 检查consultation使用次数限制
    const consultationCount = consultationHistory?.length || 0;
    const hasConsultationAccess = true;
    
    if (consultationCount >= 2 && !hasConsultationAccess) {
      setSnackbar({
        open: true,
        message: '咨询次数已达上限，正在跳转到订阅页面...',
        severity: 'warning'
      });
      // 延迟导航，让用户看到提示
      setTimeout(() => {
        navigate('/subscription-management');
      }, 2000);
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      text: messageText,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setLoading(true);

    try {
      const aiResponseData = await generateAIResponse(messageText, astrolabe, astrolabe);
      
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: aiResponseData.response,
        isUser: false,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, aiMessage]);
      
      // Save consultation to Firestore with all response formats
      await saveConsultation(messageText, aiResponseData.response, 'general', aiResponseData.fullData);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || '获取AI回复失败',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    setInputText(question);
    handleSendMessage(question);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const renderMessage = (message: Message, index: number) => {
    return (
      <Fade in={true} timeout={500 + index * 100} key={message.id}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: message.isUser ? 'flex-end' : 'flex-start',
            mb: responsiveSpacing.component,
          }}
        >
          <Paper
              sx={{
                maxWidth: '80%',
                p: responsiveSpacing.component,
                borderRadius: 4,
                backgroundColor: message.isUser ? theme.colors.primary : '#FFFFFF',
                color: message.isUser ? 'white' : theme.colors.text,
                borderBottomRightRadius: message.isUser ? 1 : 4,
                borderBottomLeftRadius: message.isUser ? 4 : 1,
                boxShadow: 2,
              }}
            >
            {!message.isUser && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: responsiveSpacing.xs }}>
                <RobotIcon sx={{ fontSize: responsiveFontSizes.body2, color: theme.colors.primary, mr: 0.5 }} />
                <Typography
                   variant="caption"
                   sx={{ fontWeight: 'bold', color: theme.colors.primary, fontSize: responsiveFontSizes.body2 }}
                 >
                   运势AI
                 </Typography>
              </Box>
            )}
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                fontSize: responsiveFontSizes.body1,
              }}
            >
              {message.text}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: responsiveSpacing.xs,
                opacity: 0.8,
                textAlign: message.isUser ? 'right' : 'left',
                fontSize: responsiveFontSizes.body2,
              }}
            >
              {message.timestamp.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Typography>
          </Paper>
        </Box>
      </Fade>
    );
  };

  const renderSuggestedQuestions = () => {
    if (messages.length > 0) return null;

    return (
      <Fade in={true} timeout={800}>
        <Box sx={{ mb: responsiveSpacing.component }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: responsiveSpacing.xs, fontSize: responsiveFontSizes.body1 }}>
            建议问题
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: responsiveSpacing.xs }}>
            {suggestedQuestions.map((question, index) => (
              <Chip
                key={index}
                label={question}
                onClick={() => handleSuggestedQuestion(question)}
                sx={{
                  backgroundColor: theme.colors.primary + '20',
                  color: theme.colors.primary,
                  fontWeight: '500',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: theme.colors.primary + '30',
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      </Fade>
    );
  };

  const renderWelcomeMessage = () => {
    if (messages.length > 0) return null;

    return (
      <Fade in={true} timeout={800}>
        <Card
          sx={{
            mb: responsiveSpacing.section,
            background: `linear-gradient(135deg, ${theme.colors.primary}20, ${theme.colors.accent}20)`,
            borderRadius: 4,
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: responsiveSpacing.component }}>
            <StarIcon sx={{ fontSize: responsiveFontSizes.h1, color: theme.colors.primary, mb: responsiveSpacing.xs }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.colors.primary, mb: responsiveSpacing.xs, fontSize: responsiveFontSizes.h4 }}>
              欢迎使用运势AI
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.6, fontSize: responsiveFontSizes.body1 }}>
              请问我关于您命运的任何问题
            </Typography>
          </CardContent>
        </Card>
      </Fade>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent})`,
          color: 'white',
          p: responsiveSpacing.component,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <RobotIcon sx={{ fontSize: responsiveFontSizes.h4, mr: responsiveSpacing.xs }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: responsiveFontSizes.h4 }}>
               AI咨询
             </Typography>
          </Box>
          <IconButton
            onClick={() => navigate('/consultation/history')}
            sx={{ color: 'white' }}
          >
            <HistoryIcon />
          </IconButton>
        </Box>
        {astrolabe && (
          <Typography variant="body2" sx={{ mt: responsiveSpacing.xs, opacity: 0.9, fontSize: responsiveFontSizes.body2 }}>
             基于您的出生命盘: {astrolabe.birthDate}
           </Typography>
        )}
      </Box>

      {/* Messages */}
      <Container maxWidth="md" sx={{ py: responsiveSpacing.component, pb: '180px' }}>
        {renderWelcomeMessage()}
        {renderSuggestedQuestions()}
        
        {messages.map((message, index) => renderMessage(message, index))}
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: responsiveSpacing.component }}>
            <Paper
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: responsiveSpacing.component,
                borderRadius: 4,
                borderBottomLeftRadius: 1,
                backgroundColor: '#FFFFFF',
                boxShadow: 2,
              }}
            >
              <CircularProgress size={20} sx={{ mr: responsiveSpacing.xs }} />
              <Typography variant="body2" sx={{ fontSize: responsiveFontSizes.body2 }}>
                 运势AI正在思考中...
               </Typography>
            </Paper>
          </Box>
        )}
        
        <div ref={messagesEndRef} />
      </Container>

      {/* Input */}
      <Paper
        sx={{
          position: 'fixed',
          bottom: { xs: '60px', sm: '50px', md: '40px' },
          left: 0,
          right: 0,
          p: responsiveSpacing.component,
          backgroundColor: '#FFFFFF',
          boxShadow: 3,
          zIndex: 1000,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: responsiveSpacing.xs }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={inputText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="询问您的运势问题"
              variant="outlined"
              inputProps={{ maxLength: 500 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 5,
                },
              }}
            />
            <IconButton
              onClick={() => handleSendMessage()}
              disabled={!inputText.trim() || loading}
              sx={
                !inputText.trim() || loading
                  ? {
                      backgroundColor: theme.colors.surface,
                      color: theme.colors.placeholder,
                      width: 40,
                      height: 40,
                    }
                  : {
                      backgroundColor: theme.colors.primary,
                      color: 'white',
                      width: 40,
                      height: 40,
                      '&:hover': {
                        backgroundColor: theme.colors.primary,
                      },
                    }
              }
            >
              <SendIcon />
            </IconButton>
          </Box>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'right',
              mt: responsiveSpacing.xs,
              color: 'text.secondary',
              fontSize: responsiveFontSizes.body2,
            }}
          >
            {inputText.length}/500 字符
          </Typography>
        </Container>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConsultationScreen;